/**
 * MobileAuditPanel — in-app dev tool.
 *
 * Activate with Cmd/Ctrl+Shift+M, or by appending `?audit=1` to any URL.
 * Scans the live DOM at the current viewport size and surfaces:
 *   • Horizontal overflow elements (content wider than viewport)
 *   • Fixed-width descendants (px-width when viewport is narrow)
 *   • Touch targets smaller than 44×44
 *   • Tab containers that aren't horizontally scrollable on narrow screens
 *   • Images without explicit max-width
 *   • Elements that render under the top safe-area inset
 *
 * Non-invasive: only mounts when explicitly toggled. No production impact.
 */
import React, { useEffect, useState, useCallback } from "react";

const TOUCH_MIN = 44;
const ENABLED_PARAM = "audit";

function _runAudit() {
  const vw = window.innerWidth;
  const safeTop = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue("--safe-top") || "0"
  ) || 0;
  const findings = {
    overflow: [],
    fixedWidth: [],
    smallTouch: [],
    tabsNotScrollable: [],
    unconstrainedImages: [],
    safeAreaViolations: [],
  };

  // 1. Horizontal overflow
  document.querySelectorAll("body *").forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > vw + 1) {
      findings.overflow.push({ tag: el.tagName, w: Math.round(rect.width), vw, sel: _selector(el), cls: el.className });
    }
    // 2. Fixed pixel widths > viewport
    const cs = getComputedStyle(el);
    const wStr = cs.width;
    if (wStr.endsWith("px") && parseFloat(wStr) > vw + 1) {
      findings.fixedWidth.push({ sel: _selector(el), width: wStr });
    }
  });

  // 3. Small touch targets (mobile only)
  if (vw < 768) {
    document.querySelectorAll("button, a[role='button'], [data-touch-target], input[type='button'], input[type='submit']").forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && rect.height < TOUCH_MIN) {
        findings.smallTouch.push({ sel: _selector(el), height: Math.round(rect.height), text: (el.textContent || "").trim().slice(0, 40) });
      }
    });
  }

  // 4. Tab containers — heuristic: roles or classnames containing 'tab'
  document.querySelectorAll("[role='tablist'], [class*='tabs']").forEach((el) => {
    const cs = getComputedStyle(el);
    if (vw < 768 && el.scrollWidth > el.clientWidth + 1 && cs.overflowX === "visible") {
      findings.tabsNotScrollable.push({ sel: _selector(el), scrollW: el.scrollWidth, clientW: el.clientWidth });
    }
  });

  // 5. Unconstrained images
  document.querySelectorAll("img").forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.width > vw + 1) {
      findings.unconstrainedImages.push({ src: el.src.slice(0, 60), w: Math.round(rect.width) });
    }
  });

  // 6. Safe-area top violations — fixed/sticky elements rendering at y=0
  // (above the safe-area-top) on iOS PWA.
  if (safeTop > 0) {
    document.querySelectorAll("[class*='sticky'], [class*='fixed']").forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const rect = el.getBoundingClientRect();
      const cs = getComputedStyle(el);
      if (rect.top < safeTop - 1 && (cs.position === "fixed" || cs.position === "sticky")) {
        const padTop = parseFloat(cs.paddingTop || "0");
        if (padTop < safeTop) {
          findings.safeAreaViolations.push({ sel: _selector(el), top: Math.round(rect.top), safeTop, padTop });
        }
      }
    });
  }

  return findings;
}

function _selector(el) {
  if (el.id) return `#${el.id}`;
  const dti = el.getAttribute("data-testid");
  if (dti) return `[data-testid="${dti}"]`;
  const cls = (el.className || "").toString().split(" ").filter(Boolean).slice(0, 2).join(".");
  return `${el.tagName.toLowerCase()}${cls ? `.${cls}` : ""}`;
}

const SECTIONS = [
  { key: "overflow",            title: "Horizontal overflow", desc: "Elements wider than viewport" },
  { key: "fixedWidth",          title: "Fixed pixel widths",  desc: "Element width > viewport" },
  { key: "smallTouch",          title: "Tiny touch targets",  desc: "Tap area < 44px (mobile only)" },
  { key: "tabsNotScrollable",   title: "Non-scrollable tabs", desc: "Tab containers without overflow-x:auto" },
  { key: "unconstrainedImages", title: "Unbounded images",    desc: "Images wider than viewport" },
  { key: "safeAreaViolations",  title: "Safe-area violations", desc: "Fixed elements rendering under status bar" },
];

export default function MobileAuditPanel() {
  const [open, setOpen] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get(ENABLED_PARAM) === "1";
    } catch {
      return false;
    }
  });
  const [findings, setFindings] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const refresh = useCallback(() => {
    setFindings(_runAudit());
  }, []);

  useEffect(() => {
    if (!open) return;
    refresh();
    const onResize = () => refresh();
    window.addEventListener("resize", onResize);
    const t = setInterval(refresh, 2000);
    return () => {
      window.removeEventListener("resize", onResize);
      clearInterval(t);
    };
  }, [open, refresh]);

  // Keyboard shortcut: Cmd/Ctrl + Shift + M
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.shiftKey && (e.key === "M" || e.key === "m")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!open) return null;

  const totals = findings
    ? Object.fromEntries(SECTIONS.map((s) => [s.key, findings[s.key].length]))
    : null;
  const total = totals ? Object.values(totals).reduce((a, b) => a + b, 0) : 0;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] bg-white border-2 border-[var(--ink)] rounded-2xl shadow-2xl text-xs font-mono max-w-[92vw] sm:max-w-md max-h-[80vh] flex flex-col"
      style={{ width: 380 }}
      data-testid="mobile-audit-panel"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-[var(--ink)] bg-[var(--ink)] text-white rounded-t-xl">
        <div className="flex items-center gap-2">
          <span>📱</span>
          <span className="font-bold">Mobile Audit</span>
          <span className="text-[10px] opacity-70">{window.innerWidth}×{window.innerHeight}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} className="text-[10px] opacity-80 hover:opacity-100" title="Rescan">↻</button>
          <button onClick={() => setOpen(false)} className="text-[10px] opacity-80 hover:opacity-100" title="Close (⌘⇧M)">✕</button>
        </div>
      </div>

      <div className="px-3 py-2 border-b border-[var(--line)] flex items-center justify-between">
        <div className={total === 0 ? "text-[var(--secondary)]" : "text-[var(--error)]"}>
          {total === 0 ? "✓ No issues found" : `${total} issue${total === 1 ? "" : "s"}`}
        </div>
        <div className="opacity-50 text-[10px]">⌘⇧M to toggle</div>
      </div>

      <div className="overflow-y-auto flex-1">
        {findings && SECTIONS.map((s) => {
          const list = findings[s.key];
          if (list.length === 0) return null;
          const isOpen = !collapsed[s.key];
          return (
            <div key={s.key} className="border-b border-[var(--line)]">
              <button
                onClick={() => setCollapsed((c) => ({ ...c, [s.key]: !c[s.key] }))}
                className="w-full text-left px-3 py-2 hover:bg-black/[0.02] flex items-center justify-between"
              >
                <div>
                  <div className="font-semibold text-[var(--ink)]">{isOpen ? "▾" : "▸"} {s.title}</div>
                  <div className="text-[10px] text-[var(--ink-3)]">{s.desc}</div>
                </div>
                <span className="text-[10px] font-bold bg-[var(--error)] text-white rounded-full px-1.5 py-0.5">{list.length}</span>
              </button>
              {isOpen && (
                <ul className="px-3 py-2 space-y-1.5">
                  {list.slice(0, 12).map((f, i) => (
                    <li key={`${f.sel || f.src || "?"}-${i}`} className="text-[10px] text-[var(--ink-2)] leading-relaxed">
                      <div className="text-[var(--primary)]">{f.sel || f.src || "?"}</div>
                      <div className="opacity-70">{JSON.stringify(f)}</div>
                    </li>
                  ))}
                  {list.length > 12 && (
                    <li className="text-[10px] text-[var(--ink-3)] italic">…and {list.length - 12} more</li>
                  )}
                </ul>
              )}
            </div>
          );
        })}
        {findings && total === 0 && (
          <div className="px-3 py-5 text-center text-[var(--ink-3)]">All clear at this viewport size.</div>
        )}
      </div>
    </div>
  );
}
